import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { LYRIC_LINE_LEAD_MS } from 'fym-shared/src/music24/lyricTiming';
import { TypedMemo } from '../../../../common/types';
import { chorusLineIndexes, hookLineIndexes, LineRange, lineClock, PickLine, rangeAfterClick } from '../fullVideoRules';
import { fullVideoMessages } from '../fullVideoMessages';
import { MomentLineRow, MomentLineTag } from './MomentLineRow';
import { useLinePlayer } from './useLinePlayer';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MomentLinePickerProps {
  accountId: number;
  itemUuid: string;
  lines: readonly PickLine[];
  range: LineRange | null;
  hookStartSec: number | null;
  lengthSec: 15 | 30;
  onPick: (range: LineRange) => void;
}

/**
 * The line list of a lyric moment (design s7b): click the first line, then the last. The picked
 * lines show tinted; the foot shows their times, the count and the length. ▶ plays the master
 * from a line with the one master player of the app.
 */
const MomentLinePickerComponent: React.FC<MomentLinePickerProps> = (props) => {
  const { accountId, itemUuid, lines, range, hookStartSec, lengthSec, onPick } = props;
  const intl = useIntl();
  const [anchor, setAnchor] = useState<number | null>(null);
  const { playFrom, isPlaying, positionSec } = useLinePlayer(accountId, itemUuid);

  const tags = useMemo(() => {
    const hook = hookLineIndexes(lines, hookStartSec);
    const chorus = chorusLineIndexes(lines);
    return new Map<number, MomentLineTag>(
      lines.map((line) => [line.index, hook.has(line.index) ? 'hook' : chorus.has(line.index) ? 'chorus' : null])
    );
  }, [hookStartSec, lines]);
  const playingIndex = useMemo(() => {
    if (!isPlaying) {
      return null;
    }
    // The display lead of the song page and the videos (fym-shared `LYRIC_LINE_LEAD_MS`).
    const ms = positionSec * 1000 + LYRIC_LINE_LEAD_MS;
    const started = lines.filter((line) => line.startMs !== null && line.startMs <= ms);
    return started[started.length - 1]?.index ?? null;
  }, [isPlaying, lines, positionSec]);

  const pick = useCallback(
    (index: number) => {
      const next = rangeAfterClick(anchor, index);
      setAnchor(next.anchor);
      onPick(next.range);
    },
    [anchor, onPick]
  );

  const first = range === null ? undefined : lines.find((line) => line.index === range.lineFrom);
  const last = range === null ? undefined : lines.find((line) => line.index === range.lineTo);
  const firstStart = first?.startMs ?? null;
  const lastStart = last?.startMs ?? null;
  const span =
    firstStart !== null && lastStart !== null
      ? `${lineClock(firstStart)} – ${lineClock(last?.endMs ?? lastStart)}`
      : null;

  return (
    <div className={styles.pickList}>
      <div className={styles.pickRows} role="group" aria-label={intl.formatMessage(fullVideoMessages.linesTitle)}>
        {lines.map((line) => (
          <MomentLineRow
            key={line.index}
            line={line}
            isPicked={range !== null && line.index >= range.lineFrom && line.index <= range.lineTo}
            isPlaying={line.index === playingIndex}
            tag={tags.get(line.index) ?? null}
            onPick={pick}
            onPlay={playFrom}
          />
        ))}
      </div>
      {range !== null && (
        <div className={styles.pickFoot}>
          {span !== null && <span className={clsx(styles.chip, styles.chipRun)}>{span}</span>}
          <span>
            {intl.formatMessage(fullVideoMessages.linesFoot, {
              count: range.lineTo - range.lineFrom + 1,
              length: lengthSec,
            })}
          </span>
        </div>
      )}
    </div>
  );
};

export const MomentLinePicker = TypedMemo(MomentLinePickerComponent);
